import React, { Component, Fragment, type JSX } from 'react';
import { Input } from './Input';
import { validateRequired } from './validate.formik';
import { ajaxLink } from '../App';
import { GetAddressFields } from '../models/AddressFields';
import { FormikProps, getIn } from 'formik';
import { FormValues } from '../components/Checkout/Checkout.interface';

interface AddressFieldsProps {
  form: FormikProps<FormValues>;
  group: string;
}

interface AddressFieldsState {
  isFetching: boolean;
}

/**
 * This component displays the address fields and handles
 * the autocompletion, by making a call to the wordpress
 * backend. Which in turn calls the postcode api. A little
 * spinner is shown while fetching the data.
 *
 * @example
 * <AddressFields group="address" form={form}/>
 */
export class AddressFields extends Component<AddressFieldsProps, AddressFieldsState> {

  constructor(props: AddressFieldsProps) {
    super(props);

    this.state = {
      isFetching: false,
    };
  }

  /**
   * Autocomplete the address using the postalCode and street number.
   */
  private async handleBlur(): Promise<void> {
    const { values, setFieldValue }: FormikProps<FormValues> = this.props.form;
    const { postalCode, houseNumber, street, city } = getIn(values, this.props.group);

    // only proceed if the postalcode and number have been provided
    // or when the street or city has already been filled in.
    if (!postalCode || !houseNumber || !!street || !!city) {
      return;
    }

    this.setState({ isFetching: true });

    try {
      const address: GetAddressFields = await $.ajax( {
        url: ajaxLink,
        type: 'POST',
        dataType: 'json',
        data: {
          action: 'get_address_from_postalcode',
          postal_code: postalCode,
          house_number: houseNumber,
        },
      });

      if (!!address.street && !!address.city) {
        setFieldValue(`${this.props.group}.street`, address.street);
        setFieldValue(`${this.props.group}.city`, address.city);
      }
    } finally {
      this.setState({ isFetching: false });
    }
  }

  /**
   * Render the address fields. When we are fetching the
   */
  public render(): JSX.Element {
    const { isFetching }: AddressFieldsState = this.state;
    return (
      <Fragment>
        <div className="row">
          <div className="col-sm-4">
            <label className="sr-only" htmlFor="Postcode_address">Postcode</label>
            <Input name={`${this.props.group}.postalCode`}
              id="Postcode_address"
              placeholder="Postcode"
              onBlur={(): Promise<void> => this.handleBlur()}
              validate={validateRequired}/>
          </div>
          <div className="col-sm-4">
            <label className="sr-only" htmlFor="Huisnummer_address">Huisnummer</label>
            <Input name={`${this.props.group}.houseNumber`}
              id="Huisnummer_address"
              placeholder="Huisnummer"
              type="number"
              onBlur={(): Promise<void> => this.handleBlur()}
              validate={validateRequired}/>
          </div>
          <div className="col-sm-4">
            <label className="sr-only" htmlFor="Toevoeging_address">Toevoeging</label>
            <Input name={`${this.props.group}.houseNumberSuffix`}
              id="Toevoeging_address"
              placeholder="Toevoeging"/>
          </div>
        </div>
        <div className="row pt-3 pb-4">
          <div className="col-sm-6 p-relative">
            <label className="sr-only" htmlFor="Straat_address">Straat</label>
            <Input name={`${this.props.group}.street`}
              id="Straat_address"
              placeholder="Straat"
              cssClass="form-control--no-edit"
              validate={validateRequired}/>
            { isFetching
            && <i className="fa fa-circle-o-notch fa-spin checkout__address-loading"/>}
          </div>
          <div className="col-sm-6 p-relative">
            <label className="sr-only" htmlFor="Plaats_address">Plaats</label>
            <Input name={`${this.props.group}.city`}
              id="Plaats_address"
              placeholder="Plaats"
              cssClass="form-control--no-edit"
              validate={validateRequired}/>
            { isFetching
            && <i className="fa fa-circle-o-notch fa-spin checkout__address-loading"/>}
          </div>
        </div>
      </Fragment>
    );
  }
}
